import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Legend
} from 'recharts';
import { BookOpen, TrendingUp, Calendar, Loader2 } from 'lucide-react';
import { on as onEvent } from '@/utils/eventBus';

const ReadingProgressChart = ({ childId, childName, ageGroup }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState([]);
  const [currentStats, setCurrentStats] = useState(null);
  const [timeRange, setTimeRange] = useState('30'); // days

  // Only show for appropriate age groups (preschool and up)
  const isAgeAppropriate = ['preschool', 'early_elementary', 'elementary', 'middle'].includes(ageGroup);

  useEffect(() => {
    if (isAgeAppropriate && childId) {
      fetchReadingProgress();
    } else {
      setLoading(false);
    }
  }, [childId, timeRange, isAgeAppropriate]);

  // Refetch after any assessment completes for this child.
  useEffect(() => {
    if (!isAgeAppropriate || !childId) return undefined;
    return onEvent('assessment:completed', (detail) => {
      if (!detail?.childId || detail.childId === childId) {
        fetchReadingProgress();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, isAgeAppropriate]);

  const fetchReadingProgress = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reading/progress/${childId}?days=${timeRange}`);
      setProgressData(response.data.history || []);
      setCurrentStats(response.data.current);
    } catch (error) {
      console.error('Failed to fetch reading progress:', error);
      // Generate sample data if no history exists
      generateSampleData();
    } finally {
      setLoading(false);
    }
  };

  const generateSampleData = () => {
    // Generate sample progress data for demo
    const today = new Date();
    const data = [];
    for (let i = parseInt(timeRange); i >= 0; i -= 7) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        lexile: Math.round(200 + (parseInt(timeRange) - i) * 2 + Math.random() * 20),
        composite: Math.round(40 + (parseInt(timeRange) - i) * 0.5 + Math.random() * 5),
        wpm: Math.round(60 + (parseInt(timeRange) - i) * 0.3 + Math.random() * 10)
      });
    }
    setProgressData(data);
    setCurrentStats({
      lexile: data[data.length - 1]?.lexile || 450,
      composite: data[data.length - 1]?.composite || 65,
      wpm: data[data.length - 1]?.wpm || 85,
      sightWordsCount: 42
    });
  };

  if (!isAgeAppropriate) {
    return null;
  }

  const getLexileLevel = (score) => {
    if (score < 200) return { label: 'Beginning Reader', color: 'text-blue-500' };
    if (score < 400) return { label: 'Early Reader', color: 'text-green-500' };
    if (score < 600) return { label: 'Transitional', color: 'text-yellow-500' };
    if (score < 800) return { label: 'Fluent Reader', color: 'text-orange-500' };
    return { label: 'Advanced', color: 'text-purple-500' };
  };

  const lexileLevel = currentStats ? getLexileLevel(currentStats.lexile) : null;
  const hasAnyData = !!(
    currentStats && (
      currentStats.lexile ||
      currentStats.composite ||
      currentStats.wpm ||
      (currentStats.sightWordsCount && currentStats.sightWordsCount > 0)
    )
  ) || progressData.length > 0;

  return (
    <Card className="col-span-full" data-testid="reading-progress-chart">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Reading Progress</CardTitle>
              <p className="text-sm text-slate-500">{childName}'s reading development</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32" data-testid="time-range-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">6 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Current Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4">
                <p className="text-xs text-emerald-600 font-medium mb-1">Lexile Score</p>
                <p className="text-2xl font-bold text-emerald-700">{currentStats?.lexile || '--'}L</p>
                {lexileLevel && (
                  <p className={`text-xs mt-1 ${lexileLevel.color}`}>{lexileLevel.label}</p>
                )}
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4">
                <p className="text-xs text-blue-600 font-medium mb-1">Composite Score</p>
                <p className="text-2xl font-bold text-blue-700">{currentStats?.composite || '--'}</p>
                <p className="text-xs text-slate-500 mt-1">out of 100</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4">
                <p className="text-xs text-amber-600 font-medium mb-1">Reading Speed</p>
                <p className="text-2xl font-bold text-amber-700">{currentStats?.wpm || '--'}</p>
                <p className="text-xs text-slate-500 mt-1">words/min</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
                <p className="text-xs text-purple-600 font-medium mb-1">Sight Words</p>
                <p className="text-2xl font-bold text-purple-700">{currentStats?.sightWordsCount || '--'}</p>
                <p className="text-xs text-slate-500 mt-1">mastered</p>
              </div>
            </div>

            {/* Progress Chart or empty state */}
            {!hasAnyData ? (
              <div className="h-64 flex flex-col items-center justify-center text-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/60 p-6" data-testid="reading-progress-empty">
                <BookOpen className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm font-semibold text-slate-700 mb-1">
                  No reading data yet
                </p>
                <p className="text-xs text-slate-500 max-w-md mb-3">
                  Complete a Lexile assessment, sight-words practice, or a timed-reading session and the chart will fill in automatically.
                </p>
                <div className="flex gap-2 flex-wrap justify-center">
                  <Button size="sm" variant="outline" asChild data-testid="reading-empty-assessment-btn">
                    <a href={`/assessment/${childId}/reading`}>Start assessment</a>
                  </Button>
                  <Button size="sm" variant="outline" asChild data-testid="reading-empty-sightwords-btn">
                    <a href={`/sight-words/${childId}`}>Sight words</a>
                  </Button>
                </div>
              </div>
            ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={progressData}>
                  <defs>
                    <linearGradient id="lexileGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0D9488" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0D9488" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="compositeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                    label={{ value: 'Lexile', angle: -90, position: 'insideLeft', fill: '#0D9488', fontSize: 12 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                    domain={[0, 100]}
                    label={{ value: 'Composite', angle: 90, position: 'insideRight', fill: '#3B82F6', fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="lexile"
                    name="Lexile Score"
                    stroke="#0D9488"
                    strokeWidth={2}
                    fill="url(#lexileGradient)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="composite"
                    name="Composite"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            )}

            {/* Trend Indicator */}
            {progressData.length > 1 && (
              <div className="flex items-center gap-2 mt-4 text-sm">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-slate-600">
                  Lexile improved by{' '}
                  <span className="font-semibold text-green-600">
                    +{Math.max(0, (progressData[progressData.length - 1]?.lexile || 0) - (progressData[0]?.lexile || 0))}L
                  </span>{' '}
                  over the selected period
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReadingProgressChart;
